import {
    Button,
} from "@mrflark/ui";
import {
    ArrowDown,
    ArrowUp,
    BarChart3,
    Bot,
    ChevronDown,
    Film,
    Image,
    RotateCcw,
    Scale,
    Shuffle,
    Sparkles,
    Tag,
    Trophy,
} from "lucide-preact";
import type {
    ReactElement,
} from "preact/compat";
import {
    useMemo,
    useState,
} from "preact/compat";

import {
    BROWSING_MODES,
} from "../../constants.ts";
import type {
    BrowsingMode,
} from "../../constants.ts";
import {
    useViewerStore,
} from "../../stores/viewerStore.ts";
import {
    Tooltip,
} from "../common/Tooltip.tsx";
import {
    AiMetadataFilters,
} from "../filters/AiMetadataFilters.tsx";
import {
    TagQueryInput,
} from "../tags/index.ts";
import {
    TagStatsPanel,
} from "../tags/index.ts";

type BrowsePanelProps = {
    onApplyFilters: () => void;
    onBrowsingModeChange: (mode: BrowsingMode) => void;
    onFetchExactCount: () => void;
    onTagStatsClick: (tagOrQuery: string) => void;
};

const BROWSING_MODE_CONFIG: Record<
    BrowsingMode,
    { icon: ReactElement; label: string; tooltip: string; }
> = {
    random: {
        icon: <Shuffle size={16} />,
        label: "Random",
        tooltip: "Random items, no repeats within session",
    },
    newest: {
        icon: <ArrowDown size={16} />,
        label: "Newest First",
        tooltip: "Most recently uploaded items first",
    },
    oldest: {
        icon: <ArrowUp size={16} />,
        label: "Oldest First",
        tooltip: "Oldest uploads first",
    },
    best: {
        icon: <Trophy size={16} />,
        label: "Best",
        tooltip: "Highest quality (Wilson score)",
    },
    controversial: {
        icon: <Scale size={16} />,
        label: "Controversial",
        tooltip: "High vote count, divisive opinions",
    },
    recommended: {
        icon: <Sparkles size={16} />,
        label: "For You",
        tooltip: "Personalized suggestions based on your votes",
    },
    untagged: {
        icon: <Tag size={16} />,
        label: "Untagged",
        tooltip: "Items awaiting tags. Sets tag query automatically",
    },
    ai_analyzed: {
        icon: <Bot size={16} />,
        label: "AI Analyzed",
        tooltip: "Items with AI-generated analysis",
    },
};

export const BrowsePanel = ({
    onApplyFilters,
    onBrowsingModeChange,
    onFetchExactCount,
    onTagStatsClick,
}: BrowsePanelProps): ReactElement => {
    const [showAiFilters, setShowAiFilters] = useState(false);

    const {
        browsingMode,
        orderedTotalCount,
        orderedIndex,
        filters,
        totalFound,
        exactCount,
        isLoadingExactCount,
        setFilters,
        clearFilters,
    } = useViewerStore();

    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (filters.media_type && filters.media_type !== "all") count++;
        if (filters.has_watermark) count++;
        if (filters.tag_query?.trim()) count++;
        return count;
    }, [filters]);

    const hasNonDefaultFilters = activeFilterCount > 0;

    const handleResetFilters = (): void => {
        clearFilters();
        onApplyFilters();
    };

    return (
        <div className="space-y-4">
            {/* --- browsing mode --- */}
            <div className="space-y-1">
                {BROWSING_MODES.map((mode) => {
                    const config = BROWSING_MODE_CONFIG[mode];
                    const isActive = mode === browsingMode;
                    return (
                        <Tooltip
                            key={mode}
                            content={config.tooltip}
                            position="right"
                            fullWidth
                        >
                            <button
                                type="button"
                                onClick={() => onBrowsingModeChange(mode)}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors ${
                                    isActive
                                        ? "bg-accent text-white font-medium"
                                        : "text-text-secondary hover:text-text-primary hover:bg-bg-tertiary"
                                }`}
                            >
                                {config.icon}
                                {config.label}
                            </button>
                        </Tooltip>
                    );
                })}
            </div>

            {browsingMode !== "random" && orderedTotalCount > 0 && (
                <div className="text-sm text-text-secondary text-center">
                    Item {orderedIndex + 1} of {orderedTotalCount}
                </div>
            )}

            <div className="border-t border-border" />

            {/* --- filters --- */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider flex items-center gap-2">
                        Filters
                        {hasNonDefaultFilters && (
                            <span className="px-1.5 py-0.5 text-xs font-medium bg-accent text-white rounded-full">
                                {activeFilterCount}
                            </span>
                        )}
                    </h3>
                    {hasNonDefaultFilters && (
                        <button
                            type="button"
                            onClick={handleResetFilters}
                            className="flex items-center gap-1 text-xs text-text-muted hover:text-text-primary transition-colors"
                        >
                            <RotateCcw size={12} />
                            Reset
                        </button>
                    )}
                </div>

                <div className="space-y-2">
                    <label className="text-sm text-text-secondary">
                        Media Type
                    </label>
                    <div className="flex rounded-lg border border-border overflow-hidden">
                        {(["all", "image", "video"] as const).map((type) => (
                            <button
                                type="button"
                                key={type}
                                onClick={() =>
                                    setFilters({
                                        media_type: type,
                                    })}
                                className={`flex-1 px-3 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                                    filters.media_type === type
                                        ? "bg-accent text-white"
                                        : "bg-bg-tertiary text-text-secondary hover:text-text-primary hover:bg-border"
                                }`}
                            >
                                {type === "image" && <Image size={14} />}
                                {type === "video" && <Film size={14} />}
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                <button
                    type="button"
                    onClick={() => setShowAiFilters(!showAiFilters)}
                    className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-secondary transition-colors"
                >
                    <BarChart3 size={12} />
                    <ChevronDown
                        size={12}
                        className={`transition-transform ${
                            showAiFilters ? "rotate-180" : ""
                        }`}
                    />
                    AI Metadata Filters
                </button>

                {showAiFilters && <AiMetadataFilters />}

                <div className="space-y-2">
                    <label className="text-sm text-text-secondary">
                        Tag Query
                    </label>
                    <TagQueryInput
                        value={filters.tag_query || ""}
                        onChange={(value) =>
                            setFilters({
                                tag_query: value,
                            })}
                        onSubmit={onApplyFilters}
                    />
                </div>

                <Button onClick={onApplyFilters} className="w-full">
                    Apply Filters
                </Button>

                {totalFound > 0 && (
                    <div className="text-sm text-text-secondary text-center">
                        {exactCount !== null
                            ? (
                                <span>
                                    {exactCount}{" "}
                                    item{exactCount !== 1 ? "s" : ""}{" "}
                                    match filters
                                </span>
                            )
                            : (
                                <span>
                                    ~{totalFound}+ item{totalFound !== 1
                                        ? "s"
                                        : ""} match filters{" "}
                                    <button
                                        type="button"
                                        onClick={onFetchExactCount}
                                        disabled={isLoadingExactCount}
                                        className="text-accent hover:text-accent/80 underline disabled:opacity-50"
                                    >
                                        {isLoadingExactCount
                                            ? "counting..."
                                            : "(get exact)"}
                                    </button>
                                </span>
                            )}
                    </div>
                )}
            </div>

            <div className="border-t border-border" />

            {/* --- tag stats --- */}
            <TagStatsPanel onTagClick={onTagStatsClick} />
        </div>
    );
};
